import type { ApiCampaign } from "@/api/perkpoint/campaigns";

export type CampaignType =
  | "Promotional"
  | "Lifecycle"
  | "Referral"
  | "Seasonal"
  | "Tier";

export type CampaignStatus =
  | "Ongoing"
  | "Scheduled"
  | "Pending approval"
  | "Ended"
  | "Draft";

export type Channel = "WhatsApp" | "Email" | "SMS" | "In-app" | "Web";

export type CampaignRow = {
  id: string;
  code: string;
  name: string;
  description: string;
  type: CampaignType;
  channels: Channel[];
  bonusPoints: string;
  startDate: string;
  endDate: string;
  status: CampaignStatus;
  /** Raw API status — used for edit lock rules. */
  apiStatus?: string;
  membersReached: string;
  pointsIssued: string;
  messages: string;
};

const CHANNEL_FROM_API: Record<string, Channel> = {
  whatsapp: "WhatsApp",
  email: "Email",
  sms: "SMS",
  in_app: "In-app",
  inapp: "In-app",
  web: "Web",
};

const TYPE_FROM_API: Record<string, CampaignType> = {
  promotional: "Promotional",
  lifecycle: "Lifecycle",
  referral: "Referral",
  seasonal: "Seasonal",
  tier: "Tier",
};

function formatCampaignDate(value: string | null | undefined): string {
  if (!value) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

function mapApiCampaignStatus(status: string | null | undefined): CampaignStatus {
  switch ((status || "").toLowerCase()) {
    case "draft":
      return "Draft";
    case "pending_approval":
      return "Pending approval";
    case "scheduled":
    case "approved":
      return "Scheduled";
    case "paused":
      // Editable like draft; surface as Scheduled so filters still make sense.
      return "Scheduled";
    case "active":
    case "ongoing":
    case "running":
    case "live":
      return "Ongoing";
    case "completed":
    case "cancelled":
    case "canceled":
    case "ended":
    case "rejected":
      return "Ended";
    default:
      return "Draft";
  }
}

function mapApiCampaignChannels(channels: ApiCampaign["channels"]): Channel[] {
  if (!channels?.length) return [];
  return channels
    .map((item) => {
      const key =
        typeof item === "string"
          ? item
          : item && typeof item === "object" && "channel" in item
            ? String(item.channel)
            : "";
      return CHANNEL_FROM_API[key.toLowerCase()];
    })
    .filter(Boolean) as Channel[];
}

function mapApiCampaignBonus(reward: ApiCampaign["reward"]): string {
  if (!reward || reward.enabled === false) return "No points offer";
  if (reward.type === "multiply" && reward.multiplier != null) {
    return `${reward.multiplier}x points`;
  }
  if (reward.type === "fixed" && reward.points != null) {
    return `${Number(reward.points).toLocaleString("en-IN")} pts`;
  }
  return "—";
}

/** Map GET /admin/programs/:id/campaigns item → table row. */
export function campaignRowFromApi(api: ApiCampaign): CampaignRow {
  const schedule = api.schedule as
    | { send_at?: string; ends_at?: string; mode?: string }
    | null
    | undefined;
  const start =
    api.starts_at ||
    api.scheduled_at ||
    (schedule?.mode === "event" ? null : schedule?.send_at) ||
    null;
  const end = api.ends_at || schedule?.ends_at || null;
  const typeKey = (api.campaign_type || "").toLowerCase();

  return {
    id: api.id,
    code: api.display_code?.trim() || api.id.slice(0, 8),
    name: api.name || "Untitled campaign",
    description: api.description?.trim() || "",
    type: TYPE_FROM_API[typeKey] ?? "Promotional",
    channels: mapApiCampaignChannels(api.channels),
    bonusPoints: mapApiCampaignBonus(api.reward),
    startDate: formatCampaignDate(start),
    endDate: formatCampaignDate(end),
    status: mapApiCampaignStatus(api.status),
    apiStatus: api.status,
    membersReached: "—",
    pointsIssued: "—",
    messages: "—",
  };
}
